import { makeWidgetProps, asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, Fragment } from 'react';
import { isEmpty } from 'lodash';
import { constants } from '@clubmed/utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { ID_LAYER_HEADER__CA__PACKAGES } from 'Components/utils/identifiers/constants';
import { LAYER_NAME_UPSELL_PACKAGES } from 'Components/utils/layers/layers.constants';
import { t } from 'Src/i18n';
import { AlternativePackageToolTip } from 'Components/widgets/package/alternative-packages/AlternativePackageToolTip';
import { ClosableLayer } from 'Components/widgets/layer/ClosableLayer';
import { PaidService } from 'Components/widgets/paid-service/PaidService';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import { StayLayerHeader } from 'Components/widgets/stay-layer-header/StayLayerHeader';
const {
  PACKAGES: { JOYVIEW_ROOM_ONLY, JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES },
} = constants;
const MAPPING_PACKAGE_INFOS = {
  [JOYVIEW_ALL_INCLUSIVE]: {
    color: COLORS.SAFFRON,
    icon: ICONS.ALL_INCLUSIVE,
    imageUrl: 'https://ns.clubmed.com/it/2019/be/joyview/Images.jpg',
    title: 'PAIBlockTitle',
  },
  [JOYVIEW_FOOD_AND_BEVERAGES]: {
    color: COLORS.ORANGE,
    icon: ICONS.DISHES,
    imageUrl: 'https://ns.clubmed.com/it/2019/be/joyview/FFB-JoyVIew.png',
    title: 'FFBBlockTitle',
  },
};
type Props = PropsWithPageTracking<{
  forwardRef: ReactRef;
  stay: StayEntity;
}>;
export const PackagesLayer = ({
  forwardRef,
  stay = makeWidgetProps<StayEntity>({}),
  tracking,
}: Props) => {
  const { booking = makeWidgetProps<BookingEntity>({}) } = stay;
  if (isEmpty(booking.packages) || isEmpty(stay.upsells?.packages)) {
    return null;
  }

  const RONPackage = stay.upsells.packages.find(({ id }) => id === JOYVIEW_ROOM_ONLY);
  const title = t('stays.upsells.packages.title');
  return (
    <ClosableLayer
      {...asWidgetProps<ComponentProps<typeof ClosableLayer>>({
        ariaLabel: title,
        ariaLabelledby: ID_LAYER_HEADER__CA__PACKAGES,
        bgColor: COLORS.WHITE,
        externalFocusTrapRefs: [forwardRef],
        hasAnimateEnter: true,
        header: (
          <StayLayerHeader
            {...asWidgetProps<ComponentProps<typeof StayLayerHeader>>({
              id: ID_LAYER_HEADER__CA__PACKAGES,
              stay: stay,
              title: title,
            })}
          />
        ),
        name: LAYER_NAME_UPSELL_PACKAGES,
        params: ['id', 'stayId'],
        tracking: tracking,
      })}
    >
      <SiteContainer
        {...asWidgetProps<ComponentProps<typeof SiteContainer>>({
          className: 'py-20',
        })}
      >
        {(booking.packages || []).map((packageUpsell, index) => {
          const packageInfos = MAPPING_PACKAGE_INFOS[packageUpsell.packageId];
          const otherPackage = stay.upsells.packages.find(
            (pck) => pck && pck.id === packageUpsell.packageId,
          );
          if (!otherPackage) {
            return null;
          }
          return (
            <Fragment key={otherPackage.id}>
              <Title
                {...asWidgetProps<ComponentProps<typeof Title>>({
                  className: 'mb-15 text-saffron',
                  theme: 'title-sans-n4',
                })}
              >
                {t(`booking.services.${packageInfos.title}`)}
              </Title>
              <PaidService
                {...asWidgetProps<ComponentProps<typeof PaidService>>({
                  headColor: packageInfos.color,
                  icon: packageInfos.icon,
                  imageUrl: packageInfos.imageUrl,
                  label: otherPackage.label,
                  price: packageUpsell.upsellPrice.formattedValue,
                  text: otherPackage.description,
                  tooltip: {
                    children: (
                      <div className="text-initial flex w-full">
                        <AlternativePackageToolTip
                          {...asWidgetProps<ComponentProps<typeof AlternativePackageToolTip>>({
                            bundle: RONPackage,
                          })}
                        />
                        <AlternativePackageToolTip
                          {...asWidgetProps<ComponentProps<typeof AlternativePackageToolTip>>({
                            bundle: otherPackage,
                          })}
                        />
                      </div>
                    ),
                    modalProps: {
                      ariaLabel: otherPackage.label,
                      closeLabel: t('common.close'),
                    },
                  },
                })}
              />
              {index < booking.packages.length - 1 && (
                <div className="mb-15 mt-20 text-center text-20 sm:mb-0 sm:mt-40">
                  {t('booking.services.JVPackagesOr')}
                </div>
              )}
            </Fragment>
          );
        })}
      </SiteContainer>
    </ClosableLayer>
  );
};
